import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useGuardStore } from "../context/GuardContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function GuardShift() {
  const navigate = useNavigate();
  const { guardId } = useParams();
  const { selectedGuard } = useGuardStore();

  const finalGuardId = guardId || selectedGuard?.id;

  const [vip, setVip] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!finalGuardId) {
      navigate("/guardshift");
      return;
    }

    async function fetchVip() {
      try {
        const token = localStorage.getItem("guardToken");

        const res = await axios.get(
          `${BASE_URL}/api/assignments/getvip/${finalGuardId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const extracted =
          res.data?.data || res.data?.vip || res.data || null;

        setVip(extracted);
      } catch (error) {
        console.error("VIP Fetch Error:", error);
        setVip(null);
      } finally {
        setLoading(false);
      }
    }

    fetchVip();
  }, [finalGuardId, navigate]);

  if (loading) {
    return (
      <div className="loading-container">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading shift details...</p>
      </div>
    );
  }

  if (!vip) {
    return (
      <div className="empty-state">
        <i className="fas fa-exclamation-triangle"></i>
        <h2>No Active Duty Assignment</h2>
        <p>Currently, you are not assigned to any VIP duty.</p>
        <button className="back-btn" onClick={() => navigate("/guarddashboard")}>
          ← Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="guard-shift-wrapper">
        {/* Header */}
        <header className="shift-header">
          <div className="header-content">
            <h1>
              <i className="fas fa-shield-alt"></i> Officer Shift Details
            </h1>
            <p>Current VIP assignment and duty schedule</p>
          </div>
        </header>

        {/* Main Card */}
        <main className="shift-main">
          <div className="shift-card">
            <div className="vip-profile">
              {/* Left: VIP Info */}
              <div className="vip-info">
                <div className="avatar-placeholder">
                  <i className="fas fa-user-tie"></i>
                </div>
                <h2 className="vip-name">{vip.name || "N/A"}</h2>
                <p className="vip-designation">{vip.designation || "N/A"}</p>
              </div>

              {/* Right: Details */}
              <div className="vip-details">
                <div className="details-grid">
                  {[
                    ["Assignment ID", vip.id || "N/A"],
                    ["VIP Name", vip.name || "N/A"],
                    ["Designation", vip.designation || "N/A"],
                    ["Shift Start", vip.startAt || "N/A"],
                    ["Shift End", vip.endAt || "N/A"],
                    ["Status", vip.status || "Active"],
                  ].map(([label, value], index) => (
                    <div className="detail-item" key={index}>
                      <span className="detail-label">{label}:</span>
                      <span className="detail-value">{value}</span>
                    </div>
                  ))}
                </div>

                <div className="action-buttons">
                  <button
                    className="btn-secondary"
                    onClick={() => navigate(-1)}
                  >
                    ← Back
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => navigate(`/guarddecision/${vip.id}`)}
                  >
                    Proceed to Duty Decision →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <style jsx>{`
        .guard-shift-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #e0e7ff 0%, #f8fbff 100%);
          color: #1e293b;
          padding: 2rem;
        }

        .shift-header {
          background: linear-gradient(135deg, #1e40af, #3b82f6);
          color: white;
          padding: 2.5rem 2rem;
          border-radius: 20px;
          margin-bottom: 2rem;
          text-align: center;
          box-shadow: 0 10px 30px rgba(30, 64, 175, 0.3);
        }

        .header-content h1 {
          font-size: 2.4rem;
          font-weight: 800;
          margin: 0 0 0.8rem 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          color:#fff;
        }

        .header-content p {
          font-size: 1.2rem;
          opacity: 0.95;
          margin: 0;
        }

        .shift-main {
          max-width: 1200px;
          margin: 0 auto;
        }

        .shift-card {
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(16px);
          border-radius: 24px;
          padding: 3rem;
          box-shadow: 0 15px 40px rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.4);
          transition: all 0.4s ease;
        }

        .shift-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 25px 50px rgba(59, 130, 246, 0.3);
        }

        .vip-profile {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 3rem;
          align-items: start;
        }

        .vip-info {
          text-align: center;
        }

        .avatar-placeholder {
          width: 160px;
          height: 160px;
          background: linear-gradient(135deg, #3b82f6, #1e40af);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: white;
          font-size: 4.5rem;
          box-shadow: 0 10px 30px rgba(59, 130, 246, 0.4);
        }

        .vip-name {
          font-size: 2.2rem;
          font-weight: 800;
          color: #1e3a8a;
          margin: 0 0 0.8rem 0;
        }

        .vip-designation {
          font-size: 1.3rem;
          color: #64748b;
          font-weight: 500;
        }

        .vip-details {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.4rem;
          margin-bottom: 2.5rem;
        }

        .detail-item {
          background: rgba(59, 130, 246, 0.08);
          padding: 1.2rem 1.6rem;
          border-radius: 14px;
          border-left: 5px solid #3b82f6;
          transition: all 0.3s ease;
        }

        .detail-item:hover {
          background: rgba(59, 130, 246, 0.15);
          transform: translateX(6px);
        }

        .detail-label {
          font-weight: 700;
          color: #1e40af;
          display: block;
          margin-bottom: 0.4rem;
          font-size: 1.05rem;
        }

        .detail-value {
          font-size: 1.1rem;
          color: #334155;
          font-weight: 600;
        }

        .action-buttons {
          display: flex;
          gap: 1.2rem;
          flex-wrap: wrap;
        }

        .btn-secondary,
        .btn-primary {
          padding: 1rem 2rem;
          border: none;
          border-radius: 14px;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        }

        .btn-secondary {
          background: #64748b;
          color: white;
        }

        .btn-secondary:hover {
          background: #475569;
          transform: translateY(-4px);
        }

        .btn-primary {
          background: #28a745;
          color: white;
        }

        .btn-primary:hover {
          background: #218838;
          transform: translateY(-4px);
          box-shadow: 0 12px 30px rgba(40, 167, 69, 0.4);
        }

        /* Loading & Empty States */
        .loading-container,
        .empty-state {
          min-height: 70vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: #64748b;
        }

        .loading-container i {
          font-size: 4rem;
          color: #3b82f6;
          margin-bottom: 1.5rem;
          animation: spin 1.5s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .empty-state i {
          font-size: 5rem;
          color: #f59e0b;
          margin-bottom: 1.5rem;
        }

        .empty-state h2 {
          font-size: 2rem;
          color: #1e40af;
          margin-bottom: 1rem;
        }

        .empty-state p {
          font-size: 1.2rem;
          margin-bottom: 2rem;
          max-width: 500px;
        }

        .back-btn {
          padding: 1rem 2rem;
          background: #1e40af;
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .back-btn:hover {
          background: #1e3a8a;
          transform: translateY(-4px);
        }

        /* Responsive */
        @media (max-width: 992px) {
          .vip-profile {
            grid-template-columns: 1fr;
            text-align: center;
          }

          .vip-details {
            margin-top: 2rem;
          }

          .details-grid {
            grid-template-columns: 1fr;
          }

          .action-buttons {
            justify-content: center;
          }
        }

        @media (max-width: 768px) {
          .guard-shift-wrapper {
            padding: 1rem;
          }

          .shift-header {
            padding: 2rem 1.5rem;
          }

          .header-content h1 {
            font-size: 2rem;
            color:#fff;
            flex-direction: column;
          }

          .shift-card {
            padding: 2rem;
          }

          .avatar-placeholder {
            width: 130px;
            height: 130px;
            font-size: 3.5rem;
          }

          .vip-name {
            font-size: 1.9rem;
          }
        }

        @media (max-width: 480px) {
          .action-buttons {
            flex-direction: column;
            width: 100%;
          }

          .btn-secondary,
          .btn-primary {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </>
  );
}


// import React, { useEffect, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import axios from "axios";
// import { useGuardStore } from "../context/GuardContext";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function GuardShift() {
//   const navigate = useNavigate();
//   const { guardId } = useParams();
//   const { selectedGuard } = useGuardStore();

//   const finalGuardId = guardId || selectedGuard?.id;

//   const [vip, setVip] = useState(null);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     if (!finalGuardId) {
//       navigate("/guardshift");
//       return;
//     }

//     async function fetchVip() {
//       try {
//         const token = localStorage.getItem("guardToken"); //  GET GUARD TOKEN

//         const res = await axios.get(
//           `${BASE_URL}/api/assignments/getvip/${finalGuardId}`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`, //  TOKEN SENT
//             },
//           }
//         );

//         const extracted =
//           res.data?.data || res.data?.vip || res.data || null;

//         setVip(extracted);
//       } catch (error) {
//         console.error("VIP Fetch Error:", error);
//         setVip(null);
//       } finally {
//         setLoading(false);
//       }
//     }

//     fetchVip();
//   }, [finalGuardId, navigate]);

//   if (loading) {
//     return (
//       <div className="text-center py-5">
//         <i className="fa fa-spinner fa-spin fa-2x text-primary mb-3"></i>
//         <h5>Loading Shift...</h5>
//       </div>
//     );
//   }

//   if (!vip) {
//     return (
//       <div style={styles.emptyBox}>
//         <h2>No VIP Assignment Found</h2>
//         <button
//           style={styles.backBtn}
//           onClick={() => navigate("/guardshift")}
//         >
//           Go Back
//         </button>
//       </div>
//     );
//   }

//   return (
//     <div style={styles.pageContainer}>
//       <div style={styles.header}>
//         <h2 style={styles.headerTitle}>
//           <i className="fa fa-shield me-2"></i> Officer Shift Details
//         </h2>
//         <p style={styles.headerSubtitle}>
//           Assigned VIP & Duty Schedule Overview
//         </p>
//       </div>

//       <div style={styles.card}>
//         <div style={styles.profileRow}>
//           <div style={styles.leftBox}>
//             <h4 style={styles.name}>{vip.name || "N/A"}</h4>
//             <p style={styles.role}>{vip.designation || "N/A"}</p>
//           </div>

//           <div style={styles.rightBox}>
//             {[
//               ["Officer Assignment ID", vip.id],
//               ["VIP Name", vip.name],
//               ["Designation", vip.designation],
//               ["Start Time", vip.startAt || "N/A"],
//               ["End Time", vip.endAt || "N/A"],
//               ["Status", vip.status || "Assigned"],
//             ].map(([label, value], i) => (
//               <div style={styles.detailRow} key={i}>
//                 <div style={styles.detailLabel}>{label}:</div>
//                 <div style={styles.detailValue}>{value}</div>
//               </div>
//             ))}

//             <div style={{ marginTop: "20px", display: "flex", gap: "12px" }}>
//               <button style={styles.backBtn} onClick={() => navigate(-1)}>
//                 ← Back
//               </button>

//               {/*  SEND vip.id TO Guard Decision */}
//               <button
//                 style={{ ...styles.backBtn, background: "#28a745" }}
//                 onClick={() => navigate(`/guarddecision/${vip.id}`)}
//               >
//                 Duty-Decision ➡
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// /* ================== UI STYLES ================== */

// const styles = {
//   pageContainer: {
//     padding: "30px",
//     minHeight: "100vh",
//     background: "rgba(255,255,255,0.15)",
//     backdropFilter: "blur(6px)",
//   },
//   header: {
//     marginBottom: "25px",
//     padding: "20px",
//     background: "linear-gradient(135deg, #4e54c8, #8f94fb)",
//     borderRadius: "14px",
//     color: "white",
//   },
//   headerTitle: { margin: 0, fontSize: "26px", fontWeight: "700" },
//   headerSubtitle: { marginTop: "5px", opacity: 0.9 },
//   card: {
//     background: "#fff",
//     borderRadius: "18px",
//     padding: "31px",
//   },
//   profileRow: { display: "flex", gap: "40px", flexWrap: "wrap" },
//   leftBox: { flex: "1", textAlign: "center" },
//   name: { fontSize: "22px", fontWeight: "700", marginTop: "50px" },
//   role: { fontSize: "15px", color: "#777" },
//   rightBox: { flex: "2" },
//   detailRow: { display: "flex", marginBottom: "12px" },
//   detailLabel: { width: "150px", fontWeight: "600" },
//   detailValue: { flex: 1 },
//   backBtn: {
//     background: "#1e73be",
//     color: "white",
//     padding: "10px 18px",
//     borderRadius: "10px",
//     border: "none",
//     cursor: "pointer",
//   },
//   emptyBox: { padding: "50px", textAlign: "center" },
// };
